import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { spRequest } from '../authConfig';
import { getAttachments } from '../graphService';
import '../styles/WinDetailModal.css';

function formatMinutes(total) {
  const mins = Math.round(Number(total) || 0);
  if (mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

export default function WinDetailModal({ win, onClose, onEdit, onDelete, currentUser, isAdmin }) {
  const { instance, accounts } = useMsal();
  const isOwner = isAdmin || (currentUser && (currentUser === win.submittedBy || currentUser === win.onBehalfOf));
  const [attachments, setAttachments] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (lightbox) setLightbox(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, lightbox]);

  useEffect(() => {
    async function fetchAttachments() {
      try {
        const tokenRes = await instance.acquireTokenSilent({
          ...spRequest,
          account: accounts[0],
        });
        const files = await getAttachments(tokenRes.accessToken, win.id);
        setAttachments(files);
      } catch {
        // silently ignore attachment fetch errors
      }
    }
    fetchAttachments();
  }, [win.id, instance, accounts]);

  // "Time Spent" is retired from new submissions but retained on historical rows.
  const hasSpent = win.timeSpentMinutes > 0;
  const hasCapacity = win.timeSavedMinutes > 0;
  const submitterLine = win.onBehalfOf && win.submittedBy
    ? `Submitted by ${win.submittedBy} on behalf of ${win.onBehalfOf}`
    : `Submitted by ${win.submittedBy || win.onBehalfOf}`;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className={`modal-panel${expanded ? ' modal-panel--expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
          <button className="modal-expand" onClick={() => setExpanded(e => !e)} aria-label="Expand">{expanded ? '⤡' : '⤢'}</button>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>

          <div className="modal-header">
            {win.department && <span className="dept-badge">{win.department}</span>}
            <span className="frequency-badge">{win.frequency}</span>
            <span className="modal-date">{formatDate(win.created)}</span>
          </div>

          <h3 className="modal-task">{win.task}</h3>

          {(hasSpent || hasCapacity) && (
            <div className="modal-stats">
              {hasSpent && (
                <div className="modal-stat">
                  <span className="modal-stat-value">{formatMinutes(win.timeSpentMinutes)}</span>
                  <span className="modal-stat-label">Time Spent</span>
                </div>
              )}
              {hasSpent && hasCapacity && <div className="modal-arrow">→</div>}
              {hasCapacity && (
                <div className="modal-stat highlight">
                  <span className="modal-stat-value">{formatMinutes(win.timeSavedMinutes)}</span>
                  <span className="modal-stat-label">Hours Added to Capacity</span>
                </div>
              )}
            </div>
          )}

          {win.howDidYouUseAI && (
            <div className="modal-section">
              <h4 className="modal-section-title">How AI Was Used</h4>
              <div className="modal-section-body modal-rich-text" dangerouslySetInnerHTML={{ __html: win.howDidYouUseAI }} />
            </div>
          )}

          {win.benefits && (
            <div className="modal-section">
              <h4 className="modal-section-title">Benefits</h4>
              <div className="modal-section-body modal-rich-text" dangerouslySetInnerHTML={{ __html: win.benefits }} />
            </div>
          )}

          {attachments.length > 0 && (
            <div className="modal-section">
              <h4 className="modal-section-title">Attachments</h4>
              <div className="attachment-grid">
                {attachments.map((file) =>
                  IMAGE_EXTS.test(file.name) ? (
                    <img
                      key={file.url}
                      className="attachment-img"
                      src={file.url}
                      alt={file.name}
                      onClick={(e) => { e.stopPropagation(); setLightbox(file); }}
                    />
                  ) : (
                    <a key={file.url} href={file.url} target="_blank" rel="noreferrer" className="attachment-link">
                      {file.name}
                    </a>
                  )
                )}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <span className="modal-author">{submitterLine}</span>
            {isOwner && !confirmDelete && (
              <div className="modal-actions">
                <button className="modal-edit-btn" onClick={onEdit}>Edit</button>
                <button className="modal-delete-btn" onClick={() => setConfirmDelete(true)}>Delete</button>
              </div>
            )}
            {isOwner && confirmDelete && (
              <div className="modal-delete-confirm">
                <span>Are you sure?</span>
                <button
                  className="modal-delete-confirm-btn"
                  disabled={deleting}
                  onClick={async () => {
                    setDeleting(true);
                    await onDelete(win.id);
                  }}
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
                <button className="modal-delete-cancel-btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="lightbox-backdrop" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">✕</button>
          <img
            className="lightbox-img"
            src={lightbox.url}
            alt={lightbox.name}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
